import { computePosition, flip, shift } from "@floating-ui/dom";
import type { Editor } from "@tiptap/react";
import { posToDOMRect, ReactRenderer } from "@tiptap/react";

import MentionList, { type MentionListHandle } from "./MentionList";

import { TemplateFormat } from "@/components/Playground/types";

interface MentionProps {
  query: string;
  editor: Editor;
  clientRect?: (() => DOMRect | null) | DOMRect | null;
  event?: KeyboardEvent;
}

interface SuggestionKeyDownProps {
  clientRect?: (() => DOMRect | null) | DOMRect | null;
  event?: KeyboardEvent;
}

const updatePosition = (editor: Editor, element: HTMLElement): void => {
  const virtualElement = {
    getBoundingClientRect: () =>
      posToDOMRect(editor.view, editor.state.selection.from, editor.state.selection.to),
  };

  computePosition(virtualElement, element, {
    placement: "bottom-start",
    strategy: "absolute",
    middleware: [shift(), flip()],
  }).then(({ x, y, strategy }) => {
    element.style.width = "max-content";
    element.style.position = strategy;
    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
    element.style.zIndex = "9999";
  });
};

const mentionSuggestion = (getOptions: () => string[], mode: TemplateFormat = "f-string") => ({
  items: ({ query }: { query: string }) => getOptions().filter(option => option.startsWith(query)),

  render: () => {
    let component: ReactRenderer<MentionListHandle> | undefined;

    return {
      onStart: (props: MentionProps) => {
        component = new ReactRenderer(MentionList, {
          props: { ...props, mode, query: props.query },
          editor: props.editor,
        });

        const clientRect =
          typeof props.clientRect === "function" ? props.clientRect() : props.clientRect;

        if (!clientRect) {
          return;
        }

        const element = component.element as HTMLElement;
        element.style.position = "absolute";
        element.style.zIndex = "9999";

        // Add a data attribute to identify this menu
        element.setAttribute("data-mention-menu", "true");

        // Add direct escape key listener as a fallback
        const handleEscape = (e: KeyboardEvent) => {
          if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            if (element.parentNode) {
              element.parentNode.removeChild(element);
            }
            component?.destroy();
          }
        };

        element.addEventListener("keydown", handleEscape, true);
        document.addEventListener("keydown", handleEscape, true);

        // Store the handler for cleanup
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (element as any).__escapeHandler = handleEscape;

        document.body.appendChild(element);
        updatePosition(props.editor, element as HTMLElement);
      },

      onUpdate(props: MentionProps) {
        component?.updateProps({ ...props, mode, query: props.query });

        const clientRect =
          typeof props.clientRect === "function" ? props.clientRect() : props.clientRect;

        if (!clientRect) {
          return;
        }

        updatePosition(props.editor, component!.element as HTMLElement);
      },

      onKeyDown(props: SuggestionKeyDownProps) {
        // Check for Escape key first
        if (props.event?.key === "Escape") {
          // Prevent default and stop propagation
          if (props.event) {
            props.event.preventDefault();
            props.event.stopPropagation();
          }

          // Destroy the component and remove from DOM
          if (component) {
            const element = component.element as HTMLElement;
            if (element && element.parentNode) {
              element.parentNode.removeChild(element);
            }
            component.destroy();
          }

          // Return true to indicate we handled the event
          return true;
        }

        // Pass other keys to the component
        if (component?.ref && props.event) {
          return component.ref.onKeyDown({ event: props.event });
        }

        return false;
      },

      onExit() {
        if (component) {
          const element = component.element as HTMLElement;

          // Remove event listeners
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const handler = (element as any).__escapeHandler;
          if (handler) {
            element.removeEventListener("keydown", handler, true);
            document.removeEventListener("keydown", handler, true);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            delete (element as any).__escapeHandler;
          }

          // Remove from DOM if still attached
          if (element && element.parentNode) {
            element.parentNode.removeChild(element);
          }

          component.destroy();
        }
      },
    };
  },
});

export default mentionSuggestion;
